import { useEffect, useState } from 'react';

import { useAppDispatch } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';
import { useDrag } from '@use-gesture/react';

import {
  type FragranceTestimony,
  FragranceTestimonyCard,
} from 'Apps/Consultation/Components/FragranceTestimonyCard';

import SliderControls from 'Components/SliderControls';

import { trackHeapEvent } from 'dux/tracking/actions';

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const Cards = styled('div')({
  position: 'relative',
});

const SlideOnClick = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '40%',
  cursor: 'pointer',
});

type SliderFragranceTestimonyCardsProps = {
  availableHeight: number;
  fragranceTestimonies: FragranceTestimony[];
  heapDomain?: string;
  isOnScreen?: boolean;
};

export const SliderFragranceTestimonyCards = ({
  availableHeight = 500,
  fragranceTestimonies,
  heapDomain,
  isOnScreen = false,
}: SliderFragranceTestimonyCardsProps) => {
  const cardsAreaHeight = availableHeight - 50;
  const fragranceTestimonyCardHeight = 200; // ±170-180 + 24px margin per card

  const dispatch = useAppDispatch();
  const cardsPerSlide = Math.max(1, Math.floor(cardsAreaHeight / fragranceTestimonyCardHeight));
  const numberOfSlides = Math.ceil(fragranceTestimonies.length / cardsPerSlide);
  const [slideIndex, setSlideIndex] = useState(0);

  const isCardShown = (testimonyIndex: number) =>
    testimonyIndex >= cardsPerSlide * slideIndex &&
    testimonyIndex < cardsPerSlide * (slideIndex + 1);

  const slideToDirection = (direction: number) => {
    const expectedNextIndex = slideIndex + direction;
    const nextIndex = Math.max(0, Math.min(expectedNextIndex, numberOfSlides - 1));
    setSlideIndex(nextIndex);
  };

  const bind = useDrag(
    ({ swipe: [swipeX] }) => {
      if (swipeX !== 0 /* either -1 or 1 */) {
        // Pulling the content to slide so inverting direction
        slideToDirection(-swipeX);
      }
    },
    {
      swipe: {
        // fine tuned these threshold values while playing with the gesture
        distance: 7,
        velocity: 0.25,
      },
    },
  );

  useEffect(() => {
    if (isOnScreen && heapDomain) {
      fragranceTestimonies.forEach((_testimony, testimonyIndex) => {
        if (isCardShown(testimonyIndex)) {
          dispatch(trackHeapEvent(`${heapDomain} - Fragrance ${testimonyIndex + 1} viewed`));
        }
      });
    }
  }, [heapDomain, isOnScreen, slideIndex]);

  return (
    <Root>
      <Cards {...bind()}>
        {fragranceTestimonies
          .filter((_testimony, testimonyIndex) => isCardShown(testimonyIndex))
          .map((card) => (
            <FragranceTestimonyCard
              key={card.title}
              by={card.by}
              dataTestId={`fragrance-testimony-card-${card.fragranceName}`}
              fragranceName={card.fragranceName}
              intensity={card.intensity}
              notes={card.notes}
              testimony={card.testimony}
              title={card.title}
            />
          ))}
        <SlideOnClick onClick={() => slideToDirection(-1)} style={{ left: 0 }} />
        <SlideOnClick onClick={() => slideToDirection(1)} style={{ right: 0 }} />
      </Cards>

      {numberOfSlides >= 2 && isOnScreen && (
        <SliderControls
          currentIndex={slideIndex}
          onClick={setSlideIndex}
          position="absolute"
          size={numberOfSlides}
          theme="rectangleWhite"
        />
      )}
    </Root>
  );
};
